import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createSuppressionApi, normalizeTerms, ruleUpdatedAtMs, splitTermKey } from '@bloo-team/api-client'
import type { SuppressionRule, SuppressionTerms } from '@bloo-team/api-client'
import { client } from '@bloo-team/core-shell'
import { isDemoSession } from '@bloo-team/core-shell'

/**
 * Suppression rules — the Detections screen's tuning layer
 * (docs/detections-requirements.md, phase 2).
 *
 * Semantics carried over from legacy verbatim: a rule belongs to one
 * detection (workbook); term values within a key are OR'd, keys are AND'd.
 * What the redesign drops: the workbook-grouped accordion (rules list flat,
 * detection is a column) and the hardcoded 12-field vocabulary (fields come
 * from the data; the term editor accepts any field name).
 *
 * Demo mode mutates an in-memory store so create/edit/delete/toggle are
 * fully exercisable without a backend; the store resets on reload.
 */

export const liveSuppressionApi = createSuppressionApi(client)

export type { SuppressionRule, SuppressionTerms }
export { splitTermKey, ruleUpdatedAtMs }

/* ------------------------------------------------------------------ display */

export interface TermView {
  key: string
  role: 'Suspect' | 'Target' | 'Other'
  field: string
  values: string[]
}

/** Stable, render-ready view of the AND-of-ORs expression. */
export function termViews(terms: SuppressionTerms): TermView[] {
  return Object.entries(terms)
    .map(([key, values]) => ({ key, ...splitTermKey(key), values }))
    .sort((a, b) => a.key.localeCompare(b.key))
}

/** 'SuspectHost = a | b AND TargetUser = c' — the one-line cell summary. */
export function termsSummary(terms: SuppressionTerms): string {
  return termViews(terms)
    .map((t) => `${t.key} = ${t.values.join(' | ')}`)
    .join('  AND  ')
}

export interface SuppressionFilter {
  text?: string
  status?: 'enabled' | 'disabled'
}

export function matchesSuppressionFilter(r: SuppressionRule, f: SuppressionFilter): boolean {
  if (f.status && (f.status === 'enabled') !== r.is_enable) return false
  if (f.text) {
    const t = f.text.trim().toLowerCase()
    if (t) {
      const hay = `${r.name} ${r.path} ${termsSummary(r.rule)} ${r.owner_id ?? ''}`.toLowerCase()
      if (!hay.includes(t)) return false
    }
  }
  return true
}

/* --------------------------------------------------------------- demo store */

function demoRule(
  id: string,
  wbId: string,
  name: string,
  rule: SuppressionTerms,
  over: Partial<SuppressionRule> = {},
): SuppressionRule {
  return {
    rule_id: id,
    wb_id: wbId,
    scope_id: 'gotham',
    name,
    path: '/Native',
    rule,
    is_enable: true,
    owner_id: 'gotham-soc',
    last_update_at: 1_755_000_000,
    ...over,
  }
}

/** Ids match demo detections (lib/detections.ts) so the per-detection
 * suppression count joins in the library slide-over. */
const DEMO_RULES_SEED: SuppressionRule[] = [
  demoRule('sup-1', 'unusual-spike-in-failed-login-attempts', 'Unusual Spike in Failed Login Attempts', {
    SuspectHost: ['scan-vault-01', 'scan-vault-02'],
    TargetUser: ['svc-backup'],
  }),
  demoRule('sup-2', 'suspicious-powershell-execution', 'Suspicious PowerShell Execution', {
    SuspectUser: ['deploy-bot'],
  }, { path: '/Native/Execution' }),
  demoRule('sup-3', 'internal-port-sweep', 'Internal Port Sweep', {
    SuspectHost: ['nessus-01'],
  }, { is_enable: false, owner_id: 'jdoe', last_update_at: 1_749_000_000 }),
]

let demoRules: SuppressionRule[] = DEMO_RULES_SEED.map((r) => ({ ...r }))
let demoSeq = 100

/** Test hook: restore the seed. */
export function resetDemoSuppressions() {
  demoRules = DEMO_RULES_SEED.map((r) => ({ ...r }))
}

const demoApi = {
  list: async () => demoRules.map((r) => ({ ...r })),
  add: async (q: { wbId: string; wbName?: string; terms: SuppressionTerms }) => {
    demoRules.push(
      demoRule(`sup-${demoSeq++}`, q.wbId, q.wbName ?? q.wbId, normalizeTerms(q.terms), {
        last_update_at: Math.floor(Date.now() / 1000),
      }),
    )
  },
  update: async (q: { ruleId: string; terms: SuppressionTerms; enabled: boolean }) => {
    demoRules = demoRules.map((r) =>
      r.rule_id === q.ruleId
        ? {
            ...r,
            rule: normalizeTerms(q.terms),
            is_enable: q.enabled,
            last_update_at: Math.floor(Date.now() / 1000),
          }
        : r,
    )
  },
  remove: async (q: { ruleId: string }) => {
    demoRules = demoRules.filter((r) => r.rule_id !== q.ruleId)
  },
}

/* ------------------------------------------------------------------- hooks */

export interface SuppressionScopeQ {
  clusterId: string
  scopeId: string
}

const listKey = (q: SuppressionScopeQ, isDemo: boolean) =>
  ['suppression-rules', q.clusterId, q.scopeId, isDemo ? 'demo' : 'live'] as const

export function useSuppressionRules(q: SuppressionScopeQ) {
  const isDemo = isDemoSession()
  return useQuery<SuppressionRule[]>({
    queryKey: listKey(q, isDemo),
    queryFn: () => (isDemo ? demoApi.list() : liveSuppressionApi.list(q)),
    staleTime: 60_000,
    retry: 1,
  })
}

export interface SaveSuppressionInput {
  /** Present = update; absent = create. */
  ruleId?: string
  wbId: string
  wbName?: string
  terms: SuppressionTerms
  enabled: boolean
}

export function useSaveSuppression(q: SuppressionScopeQ) {
  const isDemo = isDemoSession()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: SaveSuppressionInput) => {
      if (input.ruleId) {
        if (isDemo) await demoApi.update({ ruleId: input.ruleId, terms: input.terms, enabled: input.enabled })
        else await liveSuppressionApi.update({ ...q, wbId: input.wbId, ruleId: input.ruleId, terms: input.terms, enabled: input.enabled })
      } else {
        // Create is always enabled — the legacy wire contract.
        if (isDemo) await demoApi.add(input)
        else await liveSuppressionApi.add({ ...q, wbId: input.wbId, terms: input.terms })
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: listKey(q, isDemo) }),
  })
}

export function useDeleteSuppression(q: SuppressionScopeQ) {
  const isDemo = isDemoSession()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { ruleId: string; wbId: string }) => {
      if (isDemo) await demoApi.remove(input)
      else await liveSuppressionApi.remove({ ...q, ...input })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: listKey(q, isDemo) }),
  })
}

/** Toggle = update with unchanged terms — the legacy wire behavior. */
export function useToggleSuppression(q: SuppressionScopeQ) {
  const save = useSaveSuppression(q)
  return {
    ...save,
    toggle: (r: SuppressionRule) =>
      save.mutate({ ruleId: r.rule_id, wbId: r.wb_id, terms: r.rule, enabled: !r.is_enable }),
  }
}

/** rule count per detection id — the library's suppression column. */
export function suppressionCountByDetection(rules: SuppressionRule[]): Record<string, number> {
  const out: Record<string, number> = {}
  for (const r of rules) out[r.wb_id] = (out[r.wb_id] ?? 0) + 1
  return out
}
